import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import SalesPlanSelector from './components/SalesPlanSelector';
import SalesQuickCreateModal from './components/SalesQuickCreateModal';
import { BILLING_PERIODS, getDefaultBillingPeriod, getPlanQuote, getPromoBillingPeriodLabelKey, isPlanBillingPeriodEnabled } from './salesPricing';

const initialActivityForm = {
  activity_type: 'note',
  subject: '',
  description: '',
  next_followup_at: ''
};

const initialNoteForm = {
  note_type: 'general',
  title: '',
  content: ''
};

const initialMoveForm = {
  pipeline_stage_id: '',
  next_followup_at: '',
  note: ''
};

const initialConvertForm = {
  billing_email: '',
  invite_email: '',
  plan_id: '',
  billing_period: BILLING_PERIODS.MONTHLY,
  onboarding_owner_user_id: '',
  onboarding_due_days: '1',
  onboarding_note: ''
};

const SalesOpportunityDetailPage = () => {
  const { t } = useTranslation();
  const { opportunityId } = useParams();

  const [loading, setLoading] = useState(true);
  const [savingActivity, setSavingActivity] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [savingMove, setSavingMove] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [savingConvert, setSavingConvert] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [opportunity, setOpportunity] = useState(null);
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [promoCampaigns, setPromoCampaigns] = useState([]);
  const [stages, setStages] = useState([]);
  const [conversionOptions, setConversionOptions] = useState({ plans: [], users: [] });
  const [quickCreateMode, setQuickCreateMode] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [activityForm, setActivityForm] = useState(initialActivityForm);
  const [noteForm, setNoteForm] = useState(initialNoteForm);
  const [moveForm, setMoveForm] = useState(initialMoveForm);
  const [convertForm, setConvertForm] = useState(initialConvertForm);
  const [selectedPromoCampaignId, setSelectedPromoCampaignId] = useState('');

  const selectedPromoCampaign = useMemo(
    () => promoCampaigns.find(item => String(item.id) === String(selectedPromoCampaignId)) || null,
    [promoCampaigns, selectedPromoCampaignId]
  );
  const selectedPlan = useMemo(
    () => conversionOptions.plans.find(item => String(item.id) === String(convertForm.plan_id)) || null,
    [conversionOptions.plans, convertForm.plan_id]
  );
  const selectedPlanQuote = useMemo(
    () => getPlanQuote(selectedPlan, convertForm.billing_period, opportunity?.promo),
    [convertForm.billing_period, opportunity?.promo, selectedPlan]
  );

  useEffect(() => {
    if (selectedPlan && convertForm.billing_period === BILLING_PERIODS.YEARLY && !isPlanBillingPeriodEnabled(selectedPlan, BILLING_PERIODS.YEARLY)) {
      setConvertForm(previous => ({ ...previous, billing_period: BILLING_PERIODS.MONTHLY }));
    }
  }, [convertForm.billing_period, selectedPlan]);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const [
          opportunityResponse,
          activitiesResponse,
          tasksResponse,
          promoCampaignsResponse,
          stagesResponse,
          notesResponse,
          conversionOptionsResponse
        ] = await Promise.all([
          axiosInstance.get(`sales/opportunities/${opportunityId}/`),
          axiosInstance.get('sales/activities/', { params: { opportunity_id: opportunityId } }),
          axiosInstance.get('sales/activities/tasks/'),
          axiosInstance.get('sales/promo/campaigns/'),
          axiosInstance.get('sales/pipeline/stages/'),
          axiosInstance.get(`sales/opportunities/${opportunityId}/notes/`),
          axiosInstance.get('sales/opportunities/conversion-options/')
        ]);

        if (!mounted) return;

        const opportunityData = opportunityResponse.data?.opportunity || null;
        setOpportunity(opportunityData);
        setActivities(activitiesResponse.data?.activities || []);
        setTasks((tasksResponse.data?.tasks || []).filter(item => Number(item.opportunity_id) === Number(opportunityId)));
        const nextCampaigns = promoCampaignsResponse.data?.campaigns || [];
        setPromoCampaigns(nextCampaigns);
        setSelectedPromoCampaignId(nextCampaigns[0] ? String(nextCampaigns[0].id) : '');
        setStages(stagesResponse.data?.stages || []);
        setNotes(notesResponse.data?.notes || []);
        setConversionOptions({
          plans: conversionOptionsResponse.data?.plans || [],
          users: conversionOptionsResponse.data?.users || []
        });

        if (opportunityData?.company_id) {
          const [companyResponse, contactsResponse] = await Promise.all([
            axiosInstance.get(`sales/companies/${opportunityData.company_id}/`),
            axiosInstance.get(`sales/companies/${opportunityData.company_id}/contacts/`)
          ]);
          if (!mounted) return;
          setCompany(companyResponse.data?.company || null);
          setContacts(contactsResponse.data?.contacts || []);
        }
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_OPPORTUNITY'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [opportunityId, t]);

  const openMoveStageModal = () => {
    const fallbackStage = stages.find(item => Number(item.id) !== Number(opportunity?.pipeline_stage_id));
    setMoveForm({
      pipeline_stage_id: String(fallbackStage?.id || ''),
      next_followup_at: opportunity?.next_followup_at ? new Date(new Date(opportunity.next_followup_at).getTime() - new Date(opportunity.next_followup_at).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '',
      note: ''
    });
    setShowMoveModal(true);
  };

  const createActivity = async event => {
    event.preventDefault();
    if (!opportunity) return;

    setSavingActivity(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post('sales/activities/', {
        company_id: opportunity.company_id,
        opportunity_id: opportunity.id,
        activity_type: activityForm.activity_type,
        subject: activityForm.subject,
        description: activityForm.description,
        next_followup_at: activityForm.next_followup_at
      });
      const newActivity = response.data?.activity;
      if (newActivity) {
        setActivities(previous => [newActivity, ...previous]);
      }
      setOpportunity(previous => (
        previous
          ? {
              ...previous,
              next_followup_at: activityForm.next_followup_at,
              last_contact_at: new Date().toISOString()
            }
          : previous
      ));
      setActivityForm(initialActivityForm);
      setSuccess(t('SALES.ACTIVITY_CREATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_ACTIVITY'));
    } finally {
      setSavingActivity(false);
    }
  };

  const createNote = async event => {
    event.preventDefault();
    setSavingNote(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${opportunityId}/notes/`, noteForm);
      const newNote = response.data?.note;
      if (newNote) {
        setNotes(previous => [newNote, ...previous]);
      }
      setNoteForm(initialNoteForm);
      setSuccess(t('SALES.NOTE_CREATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_NOTE'));
    } finally {
      setSavingNote(false);
    }
  };

  const submitMoveStage = async event => {
    event.preventDefault();
    if (!moveForm.pipeline_stage_id) return;

    setSavingMove(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${opportunityId}/move-stage/`, {
        pipeline_stage_id: Number(moveForm.pipeline_stage_id),
        next_followup_at: moveForm.next_followup_at,
        note: moveForm.note
      });
      if (response.data?.opportunity) {
        setOpportunity(response.data.opportunity);
      }
      setShowMoveModal(false);
      setSuccess(t('SALES.MOVE_STAGE_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_MOVE_STAGE'));
    } finally {
      setSavingMove(false);
    }
  };

  const assignPromo = async event => {
    event.preventDefault();
    if (!selectedPromoCampaignId || !opportunity?.id) return;

    setSavingPromo(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${opportunityId}/promo/`, {
        action: 'assign',
        campaign_id: Number(selectedPromoCampaignId),
      });
      if (response.data?.promo) {
        setOpportunity(previous => (previous ? { ...previous, promo: response.data.promo } : previous));
      }
      setShowPromoModal(false);
      setSuccess(t('SALES.PROMO_ASSIGNED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_ASSIGN_PROMO'));
    } finally {
      setSavingPromo(false);
    }
  };

  const removePromo = async () => {
    setSavingPromo(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${opportunityId}/promo/`, {
        action: 'remove',
      });
      setOpportunity(previous => (previous ? { ...previous, promo: response.data?.promo || null } : previous));
      setSuccess(t('SALES.PROMO_CANCELLED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CANCEL_PROMO'));
    } finally {
      setSavingPromo(false);
    }
  };

  const openConvertModal = () => {
    const firstPlan = conversionOptions.plans[0] || null;
    const firstUser = conversionOptions.users[0] || null;
    setConvertForm({
      billing_email: '',
      invite_email: '',
      plan_id: firstPlan ? String(firstPlan.id) : '',
      billing_period: getDefaultBillingPeriod(opportunity?.promo, firstPlan),
      onboarding_owner_user_id: firstUser ? String(firstUser.id) : '',
      onboarding_due_days: '1',
      onboarding_note: ''
    });
    setShowConvertModal(true);
  };

  const convertOpportunity = async event => {
    event.preventDefault();
    setSavingConvert(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/opportunities/${opportunityId}/convert/`, {
        billing_email: convertForm.billing_email || undefined,
        invite_email: convertForm.invite_email || undefined,
        plan_id: convertForm.plan_id ? Number(convertForm.plan_id) : undefined,
        billing_period: convertForm.billing_period,
        onboarding_owner_user_id: convertForm.onboarding_owner_user_id ? Number(convertForm.onboarding_owner_user_id) : undefined,
        onboarding_due_days: convertForm.onboarding_due_days ? Number(convertForm.onboarding_due_days) : undefined,
        onboarding_note: convertForm.onboarding_note || undefined
      });
      if (response.data?.opportunity) {
        setOpportunity(response.data.opportunity);
      }
      setShowConvertModal(false);
      setSuccess(t('SALES.CONVERT_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CONVERT_OPPORTUNITY'));
    } finally {
      setSavingConvert(false);
    }
  };

  const handleCreated = payload => {
    if (payload?.type === 'task' && payload.task) {
      setTasks(previous => [payload.task, ...previous]);
    }
    if (payload?.type === 'contact' && payload.contact) {
      setContacts(previous => [...previous, payload.contact]);
    }
    if (payload?.type === 'company' && payload.company) {
      setCompany(payload.company);
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.OPPORTUNITY_DETAIL')} className="mb-3" />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center">
          <Spinner animation="border" />
        </Card>
      ) : (
        <>
          <Card className="mb-3 border-primary-subtle">
            <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div className="text-primary fw-semi-bold">{t('SALES.DEAL_COMMAND_CENTER')}</div>
                <div className="text-600 fs--1">{t('SALES.DEAL_COMMAND_CENTER_HELP')}</div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setQuickCreateMode('task')}>{t('SALES.NEW_TASK')}</Button>
                <Button size="sm" variant="outline-primary" onClick={() => setQuickCreateMode('contact')}>{t('SALES.NEW_CONTACT')}</Button>
                <Button size="sm" variant="outline-primary" onClick={openMoveStageModal}>{t('SALES.MOVE_STAGE')}</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setShowPromoModal(true)}>{opportunity?.promo ? t('SALES.CHANGE_PROMO') : t('SALES.ASSIGN_PROMO_CODE')}</Button>
                <Button size="sm" variant="outline-success" onClick={openConvertModal} disabled={opportunity?.status === 'won'}>{t('SALES.CONVERT')}</Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Body>
              <Row className="g-3 align-items-start">
                <Col xl={4}>
                  <div className="fw-semi-bold fs-1">{opportunity?.title || '-'}</div>
                  <div className="text-600 fs--1">{company?.name || opportunity?.company_name || '-'}</div>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.CURRENT_STAGE')}</div>
                  <div className="fw-semi-bold">{opportunity?.pipeline_stage_name || t('SALES.NO_STAGE')}</div>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.PRIORITY_OR_HEAT')}</div>
                  <Badge bg={opportunity?.interest_level === 'hot' ? 'danger' : opportunity?.interest_level === 'warm' ? 'warning' : 'primary'}>
                    {opportunity?.interest_level || 'cold'}
                  </Badge>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.OWNER')}</div>
                  <div className="fw-semi-bold">{opportunity?.owner_name || '-'}</div>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.PROBABILITY')}</div>
                  <div className="fw-semi-bold">{opportunity?.probability_percent || 0}%</div>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.EXPECTED_CLOSE')}</div>
                  <div className="fw-semi-bold">{opportunity?.expected_close_date || '-'}</div>
                </Col>
                <Col sm={6} xl={2}>
                  <div className="text-600 fs--2">{t('SALES.NEXT_FOLLOWUP')}</div>
                  <div className={`fw-semi-bold ${opportunity?.next_followup_at && new Date(opportunity.next_followup_at) < new Date() ? 'text-danger' : ''}`}>
                    {opportunity?.next_followup_at ? new Date(opportunity.next_followup_at).toLocaleString() : t('SALES.NO_NEXT_STEP')}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="g-3">
            <Col xl={8}>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <span className="fw-semi-bold">{t('SALES.NOTES_SECTION')}</span>
                      <Badge bg="soft-info" text="info">{notes.length}</Badge>
                    </Card.Header>
                    <Card.Body className="pt-2">
                      {notes.length ? notes.map(note => (
                        <div key={note.id} className="py-2 border-bottom">
                          <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="fw-semi-bold">{note.title}</div>
                            <Badge bg="soft-secondary" text="secondary">{note.note_type}</Badge>
                          </div>
                          <div className="text-700 fs--1 mt-1">{note.content}</div>
                          <div className="text-600 fs--2 mt-1">{note.author_name || '-'} · {note.updated_at ? new Date(note.updated_at).toLocaleString() : '-'}</div>
                        </div>
                      )) : <div className="text-600">{t('SALES.EMPTY_NOTES')}</div>}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header className="fw-semi-bold">{t('SALES.CONTACTS')}</Card.Header>
                    <Card.Body className="pt-2">
                      {contacts.length ? contacts.map(contact => (
                        <div key={contact.id} className="py-2 border-bottom">
                          <div className="fw-semi-bold">{contact.full_name}</div>
                          <div className="text-600 fs--1">{contact.role || '-'}</div>
                          <div className="text-600 fs--1">{contact.email || '-'}</div>
                          <div className="text-600 fs--1">{contact.phone || '-'}</div>
                        </div>
                      )) : <div className="text-600">{t('SALES.EMPTY_CONTACTS_OPPORTUNITY')}</div>}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.PENDING_TASKS')}</span>
                  <Button size="sm" variant="outline-primary" onClick={() => setQuickCreateMode('task')}>{t('SALES.NEW_TASK')}</Button>
                </Card.Header>
                <Card.Body className="pt-2">
                  {tasks.length ? tasks.map(task => (
                    <div key={task.id} className="py-2 border-bottom d-flex justify-content-between align-items-center gap-2">
                      <div>
                        <div className="fw-semi-bold">{task.title}</div>
                        <div className={`fs--1 ${task.due_at && new Date(task.due_at) < new Date() ? 'text-danger' : 'text-600'}`}>
                          {task.due_at ? new Date(task.due_at).toLocaleString() : '-'}
                        </div>
                      </div>
                      <Badge bg={task.status === 'done' ? 'success' : task.due_at && new Date(task.due_at) < new Date() ? 'danger' : 'warning'}>
                        {task.priority}
                      </Badge>
                    </div>
                  )) : <div className="text-600">{t('SALES.EMPTY_TASKS_OPPORTUNITY')}</div>}
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.PROMO')}</span>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => setShowPromoModal(true)}>{opportunity?.promo ? t('SALES.CHANGE_PROMO') : t('SALES.ASSIGN_PROMO_CODE')}</Button>
                    {opportunity?.promo?.status === 'reserved' ? (
                      <Button size="sm" variant="outline-danger" onClick={removePromo} disabled={savingPromo}>{t('SALES.REMOVE_PROMO')}</Button>
                    ) : null}
                  </div>
                </Card.Header>
                <Card.Body className="pt-2">
                  {opportunity?.promo ? (
                    <Row className="g-3 align-items-center">
                      <Col md={4}>
                        <div className="text-600 fs--2">{t('SALES.CAMPAIGN')}</div>
                        <div className="fw-semi-bold">{opportunity.promo.campaign_name}</div>
                      </Col>
                      <Col md={3}>
                        <div className="text-600 fs--2">{t('SALES.STATUS')}</div>
                        <Badge bg={opportunity.promo.status === 'redeemed' ? 'success' : opportunity.promo.status === 'reserved' ? 'warning' : 'secondary'}>{opportunity.promo.status}</Badge>
                      </Col>
                      <Col md={2}>
                        <div className="text-600 fs--2">{t('SALES.PROMO_CODE')}</div>
                        <div className="fw-semi-bold">{opportunity.promo.code}</div>
                      </Col>
                      <Col md={3}>
                        <div className="text-600 fs--2">{t('SALES.PROMO_EXPIRES')}</div>
                        <div className={`fw-semi-bold ${opportunity.promo.expires_soon ? 'text-danger' : ''}`}>
                          {opportunity.promo.valid_until ? new Date(opportunity.promo.valid_until).toLocaleDateString() : '-'}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-600 fs--2">{t('SALES.BILLING_PERIOD')}</div>
                        <Badge bg={opportunity.promo.applicable_billing_period === 'yearly' ? 'dark' : opportunity.promo.applicable_billing_period === 'monthly' ? 'info' : 'secondary'}>
                          {t(getPromoBillingPeriodLabelKey(opportunity.promo.applicable_billing_period))}
                        </Badge>
                      </Col>
                    </Row>
                  ) : <div className="text-600">{t('SALES.EMPTY_PROMOS_OPPORTUNITY')}</div>}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.ACTIVITY_TIMELINE')}</Card.Header>
                <Card.Body className="pt-2">
                  {activities.length ? activities.map(activity => (
                    <div key={activity.id} className="py-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <div className="fw-semi-bold">{activity.subject || activity.activity_type}</div>
                        <Badge bg="soft-info" text="info">{activity.activity_type}</Badge>
                      </div>
                      <div className="text-700 fs--1 mt-1">{activity.description}</div>
                      <div className="text-600 fs--2 mt-1">
                        {activity.happened_at ? new Date(activity.happened_at).toLocaleString() : '-'}
                        {activity.next_followup_at ? ` · ${t('SALES.NEXT_FOLLOWUP')}: ${new Date(activity.next_followup_at).toLocaleString()}` : ''}
                      </div>
                    </div>
                  )) : <div className="text-600">{t('SALES.EMPTY_ACTIVITIES')}</div>}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="mb-3">
                <Card.Header className="fw-semi-bold">{t('SALES.LOG_ACTIVITY')}</Card.Header>
                <Card.Body>
                  <Form onSubmit={createActivity}>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.ACTIVITY_TYPE')}</Form.Label>
                      <Form.Select
                        value={activityForm.activity_type}
                        onChange={event => setActivityForm(previous => ({ ...previous, activity_type: event.target.value }))}
                      >
                        <option value="note">note</option>
                        <option value="call">call</option>
                        <option value="meeting">meeting</option>
                        <option value="email">email</option>
                        <option value="demo">demo</option>
                        <option value="proposal_sent">proposal_sent</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.SUBJECT')}</Form.Label>
                      <Form.Control
                        value={activityForm.subject}
                        onChange={event => setActivityForm(previous => ({ ...previous, subject: event.target.value }))}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.DESCRIPTION')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={activityForm.description}
                        onChange={event => setActivityForm(previous => ({ ...previous, description: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('SALES.NEXT_FOLLOWUP')}</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={activityForm.next_followup_at}
                        onChange={event => setActivityForm(previous => ({ ...previous, next_followup_at: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Button type="submit" disabled={savingActivity}>
                      {savingActivity ? t('SALES.SAVING') : t('SALES.SUBMIT')}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.ADD_NOTE')}</Card.Header>
                <Card.Body>
                  <Form onSubmit={createNote}>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.NOTE_TYPE')}</Form.Label>
                      <Form.Select
                        value={noteForm.note_type}
                        onChange={event => setNoteForm(previous => ({ ...previous, note_type: event.target.value }))}
                      >
                        <option value="general">general</option>
                        <option value="meeting_summary">meeting_summary</option>
                        <option value="pain_point">pain_point</option>
                        <option value="objection">objection</option>
                        <option value="internal">internal</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.TITLE')}</Form.Label>
                      <Form.Control
                        value={noteForm.title}
                        onChange={event => setNoteForm(previous => ({ ...previous, title: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('SALES.DESCRIPTION')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={noteForm.content}
                        onChange={event => setNoteForm(previous => ({ ...previous, content: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Button type="submit" variant="outline-primary" disabled={savingNote}>
                      {savingNote ? t('SALES.SAVING') : t('SALES.SAVE_NOTE')}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <SalesQuickCreateModal
        show={Boolean(quickCreateMode)}
        mode={quickCreateMode || 'task'}
        onHide={() => setQuickCreateMode('')}
        companies={company ? [company] : []}
        opportunities={opportunity ? [opportunity] : []}
        stages={stages}
        defaultCompanyId={opportunity?.company_id}
        defaultOpportunityId={opportunity?.id}
        onCreated={handleCreated}
        onRequestModeChange={setQuickCreateMode}
      />

      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)} centered>
        <Form onSubmit={submitMoveStage}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.MOVE_STAGE')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.PIPELINE_STAGE')}</Form.Label>
              <Form.Select
                value={moveForm.pipeline_stage_id}
                onChange={event => setMoveForm(previous => ({ ...previous, pipeline_stage_id: event.target.value }))}
                required
              >
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {stages.map(stage => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.NEXT_FOLLOWUP')}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={moveForm.next_followup_at}
                onChange={event => setMoveForm(previous => ({ ...previous, next_followup_at: event.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('SALES.NOTE')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={moveForm.note}
                onChange={event => setMoveForm(previous => ({ ...previous, note: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMoveModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" disabled={savingMove}>{savingMove ? t('SALES.SAVING') : t('SALES.SAVE_CHANGES')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showPromoModal} onHide={() => setShowPromoModal(false)} centered>
        <Form onSubmit={assignPromo}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.ASSIGN_PROMO_CODE')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t('SALES.CAMPAIGN')}</Form.Label>
              <Form.Select value={selectedPromoCampaignId} onChange={event => setSelectedPromoCampaignId(event.target.value)} required>
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {promoCampaigns.filter(item => item.is_active).map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {selectedPromoCampaign ? (
              <div className="mt-3 text-600 fs--1">
                <div>{selectedPromoCampaign.description || t('SALES.NO_DESCRIPTION')}</div>
                <Badge bg={selectedPromoCampaign.applicable_billing_period === 'yearly' ? 'dark' : selectedPromoCampaign.applicable_billing_period === 'monthly' ? 'info' : 'secondary'} className="mt-2">
                  {t(getPromoBillingPeriodLabelKey(selectedPromoCampaign.applicable_billing_period))}
                </Badge>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPromoModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" disabled={savingPromo}>{savingPromo ? t('SALES.SAVING') : t('SALES.ASSIGN')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} centered>
        <Form onSubmit={convertOpportunity}>
          <Modal.Header closeButton>
            <Modal.Title>{t('SALES.CONVERT')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.BILLING_EMAIL')}</Form.Label>
              <Form.Control
                type="email"
                value={convertForm.billing_email}
                onChange={event => setConvertForm(previous => ({ ...previous, billing_email: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.INVITE_EMAIL')}</Form.Label>
              <Form.Control
                type="email"
                value={convertForm.invite_email}
                onChange={event => setConvertForm(previous => ({ ...previous, invite_email: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.PROMO')}</Form.Label>
              <Form.Control value={opportunity?.promo ? `${opportunity.promo.campaign_name} · ${opportunity.promo.code}` : t('SALES.NO_PROMO')} disabled readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <SalesPlanSelector
                plans={conversionOptions.plans}
                selectedPlanId={convertForm.plan_id}
                onSelectPlanId={planId => {
                  const nextPlan = conversionOptions.plans.find(item => String(item.id) === String(planId));
                  setConvertForm(previous => ({
                    ...previous,
                    plan_id: planId,
                    billing_period: getDefaultBillingPeriod(opportunity?.promo, nextPlan)
                  }));
                }}
                billingPeriod={convertForm.billing_period}
                onBillingPeriodChange={value => setConvertForm(previous => ({ ...previous, billing_period: value }))}
                promo={opportunity?.promo || null}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.ONBOARDING_OWNER')}</Form.Label>
              <Form.Select
                value={convertForm.onboarding_owner_user_id}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_owner_user_id: event.target.value }))}
              >
                <option value="">{t('SALES.SELECT_ONE')}</option>
                {conversionOptions.users.map(item => (
                  <option key={item.id} value={item.id}>{item.full_name || item.username}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('SALES.ONBOARDING_DUE_DAYS')}</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={30}
                value={convertForm.onboarding_due_days}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_due_days: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('SALES.NOTE')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={convertForm.onboarding_note}
                onChange={event => setConvertForm(previous => ({ ...previous, onboarding_note: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConvertModal(false)}>{t('SALES.CANCEL')}</Button>
            <Button type="submit" variant="success" disabled={savingConvert || Boolean(selectedPlanQuote && !selectedPlanQuote.isCompatible)}>{savingConvert ? t('SALES.SAVING') : t('SALES.CONVERT')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SalesOpportunityDetailPage;
